import { FC, FormEvent, InputHTMLAttributes, useState, useId } from 'react'
import Icon from './Icon'

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  name: string
  onUpdate: (value: string) => void
  validators?: { validator: (value: string) => boolean; message: string }[]
}

const TextInput: FC<Props> = ({
  className = '',
  type = 'text',
  label,
  name,
  value,
  onUpdate,
  validators = [],
  ...rest
}) => {
  // returns string if there is an error or undefined if there is no error
  const getErrorMessage = (value: string) => {
    try {
      return validators.find(one => !one.validator(value))?.message
    } catch (err) {
      return (err as Error).message
    }
  }

  const uniqueId = useId()
  const id = `${type}-input-${name}`

  const [touched, setTouched] = useState(false)
  const [focus, setFocus] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    getErrorMessage(String(value))
  )
  const showError = errorMessage && !focus && touched

  const handleUpdate = (e: FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value
    const errMsg = getErrorMessage(value)
    setErrorMessage(errMsg)
    onUpdate(value)
  }

  const handleFocus = () => {
    setFocus(true)
    setTouched(true)
    setErrorMessage(getErrorMessage(String(value)))
  }

  return (
    <div className={`FormControl ${showError ? 'is-error' : ''} ${className}`}>
      <label className="FormLabel mb-1" htmlFor={`${id}${uniqueId}`}>
        {label}
      </label>
      <input
        className="FormInput"
        type={type}
        id={`${id}${uniqueId}`}
        data-testid={id}
        defaultValue={value}
        onChange={handleUpdate}
        onBlur={() => setFocus(false)}
        onFocus={handleFocus}
        {...rest}
      />
      {showError && (
        <span className="FormElementHelp is-error mt-1">
          <Icon
            variant="error"
            className="Icon--sm mr-1"
            style={{ verticalAlign: 'top' }}
          />
          {errorMessage}
        </span>
      )}
    </div>
  )
}

export default TextInput
